<template>
  <div class="container-fluid pt-0 mt-2">
    <!-- Loading Indicator -->
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPage"
      background-color="#f3f3f3"
      :opacity="0.8"
      loader="bars"
    ></loading>

    <h5 class="mb-4 font-weight-bold">{{ sessionName }}</h5>

    <!-- Table start -->
    <div
      style="
        background-color: #fff;
        max-height: 40rem;
        overflow-y: auto;
        width: 100%;
        height: fit-content;
        margin-top: 2rem;
      "
    >
      <div class="card-body" style="overflow-x: auto; width: 100%">
        <!-- dropdown + search -->
        <div class="table-responsive">
          <div class="row mb-2">
            <div class="col-12">
              <div
                class="d-flex align-items-center justify-content-between"
                style="gap: 0.4rem"
              >
                <div
                  style="
                    width: 14rem;
                    background: #fff;
                    border-radius: 8px;
                    border: 1px solid rgba(183, 195, 205, 1);
                  "
                >
                  <select
                    class="form-control"
                    v-model="statusFilter"
                    @change="handleStatusChange"
                    style="background: transparent !important; cursor: pointer"
                  >
                    <option value="all">{{ $t("all_devices") }}</option>
                    <option value="critical">{{ $t("critical") }}</option>
                    <option value="stable">{{ $t("stable") }}</option>
                  </select>
                </div>

                <TableSearch @handleSearch="handleSearch" />
              </div>
            </div>
          </div>
        </div>

        <!-- table + pagination -->
        <div class="row">
          <div class="col-12">
            <div ref="content" class="table-responsive">
              <table id="session-history" class="table table-hover display">
                <thead style="background: rgba(240, 244, 251, 0.6)">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        :checked="
                          selectedRows.length === paginatedData.length &&
                          paginatedData.length > 0
                        "
                        @click="toggleSelectAll"
                      />
                    </th>
                    <th>{{ $t("status") }}</th>
                    <th>{{ $t("last_seen") }}</th>
                    <th>{{ $t("device_type") }}</th>
                    <th>{{ $t("device_name") }}</th>
                    <th>IMEI</th>
                    <!-- <th>{{ $t("excursion") }}</th> -->
                    <th>{{ $t("temperature") }}</th>
                    <th>{{ $t("humidity") }}</th>
                    <th>{{ $t("battery") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in paginatedData"
                    :key="row.id"
                    :class="{ 'data-table-bg': idx % 2 }"
                    @click="gotodevice(row.id)"
                    style="cursor: pointer; text-align: left"
                  >
                    <td>
                      <input
                        type="checkbox"
                        :checked="selectedRows.includes(row.id)"
                        @change="toggleRowSelection(row.id)"
                      />
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <span
                          class="status-indicator"
                          :class="getStatusClass(row)"
                        ></span>
                        <span class="ml-2">{{ row.deviceStatus }}</span>
                      </div>
                    </td>
                    <td>{{ formatDate(row.updatedAt) }}</td>
                    <td class="text-capitalize">
                      {{ row.type || "N/A" }}
                    </td>
                    <!-- Static as device type isn't in the data -->
                    <td>{{ row.settings.name || "N/A" }}</td>
                    <td>{{ row.imei || "N/A" }}</td>
                    <!-- <td
                      :class="{
                        'text-danger': getExcursionValue(
                          row,
                          'isUpperLimitOrHeatingExcursion'
                        ),
                      }"
                    >
                      {{
                        getExcursionValue(
                          row,
                          "heatingOrFreezingExcursionValue"
                        )
                      }}
                    </td> -->
                    <td :class="getTemperatureClass(row)">
                      {{
                        row.temperature
                          ? row.temperature.toFixed(1) + "°C"
                          : "N/A"
                      }}
                    </td>
                    <td>{{ row.humidity ? row.humidity + "%" : "N/A" }}</td>
                    <td>
                      <div
                        class="battery-indicator"
                        :class="getBatteryClass(row.battery)"
                      >
                        {{ row.battery ? `${row.battery}%` : "N/A" }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!paginatedData.length">
                    <td :colspan="10" class="text-center py-4">
                      {{ $t("no_data_found") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Pagination
              :pageCount="paginationMeta.totalPages || 1"
              :data="devices"
              :sizeperpage="sizeperpage"
              :pageNumber="pageNumber"
              :pages="pages"
              rowOption="table"
              @goToLastPage="goToLastPage"
              @nextPage="nextPage"
              @handlePageChange="handlePageChange"
              @goToFirstPage="goToFirstPage"
              @prevPage="prevPage"
              @onchange="onchange"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Table end -->
  </div>
</template>

<script>
import TableSearch from "@/components/TableSearch.vue";
import Pagination from "@/components/PaginationNew.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { mapActions, mapGetters } from "vuex";
import tableMixin from "../../mixins/tableMixinWithBackend";
import moment from "moment";

export default {
  name: "DeviceSessionHistory",
  components: { TableSearch, Pagination, Loading },
  mixins: [tableMixin],
  data() {
    return {
      statusFilter: "all",
      searchQuery: "",
      session: {
        id: null,
        name: "",
        startDate: null,
        endDate: null,
        devices: [],
      },
      columns: [
        { key: "status", label: "Status" },
        { key: "lastSeen", label: "Last Seen" },
        { key: "deviceType", label: "Device Type" },
        { key: "deviceName", label: "Device Name" },
        { key: "imei", label: "IMEI" },
        // { key: "excursion", label: "Excursion" },
        { key: "temperature", label: "Temperature" },
        { key: "humidity", label: "Humidity" },
        { key: "battery", label: "Battery" },
      ],
      selectedRows: [],
      pageNumber: 1,
      sizeperpage: 10,
      loading: false,
      isLoading: false,
      fullPage: true,
      dataTable: [],
    };
  },
  computed: {
    ...mapGetters([
      "sessionHistory",
      "selectedSession",
      "historyMeta",
      "devices",
      "devicespaginationMeta",
    ]),
    sessionName() {
      return this.session.name || `Session ${this.session.id || ""}`;
    },
    tableColumns() {
      return this.columns.map((col) => col.key);
    },
    paginationMeta() {
      return this.devicespaginationMeta;
    },
    params() {
      const devices = this.session.devices
        .map((device) => device._id)
        .join(",");
      return {
        deviceIds: this.session.devices.map((device) => device._id),
        startDate: this.session.startDate
          ? moment(this.session.startDate).format("YYYY-MM-DD")
          : undefined,
        endDate: this.session.endDate
          ? moment(this.session.endDate).format("YYYY-MM-DD")
          : undefined,
        page: this.pageNumber,
        limit: this.sizeperpage,
        status: this.statusFilter === "all" ? undefined : this.statusFilter,
        keyword: this.searchQuery,
      };
    },
  },
  methods: {
    ...mapActions([
      "fetchSessionHistory",
      "clearSelectedSession",
      "fetchallDevices",
    ]),
    gotodevice(deviceTag) {
      this.$router.push(`/devices/${deviceTag}/details`);
    },
    toggleRow(id) {
      const index = this.selectedRows.indexOf(id);
      if (index === -1) {
        this.selectedRows.push(id);
      } else {
        this.selectedRows.splice(index, 1);
      }
    },
    toggleSelectAll() {
      if (this.selectedRows.length === this.devices.length) {
        this.selectedRows = [];
      } else {
        this.selectedRows = this.devices.map((device) => device.id);
      }
    },
    toggleRowSelection(id) {
      const index = this.selectedRows.indexOf(id);
      if (index === -1) {
        this.selectedRows.push(id);
      } else {
        this.selectedRows.splice(index, 1);
      }
    },
    handlePageChange(n) {
      this.pageNumber = n;
    },
    nextPage() {
      if (this.pageNumber < this.pageCount) this.pageNumber++;
    },
    prevPage() {
      if (this.pageNumber > 1) this.pageNumber--;
    },
    goToFirstPage() {
      this.pageNumber = 1;
    },
    goToLastPage() {
      this.pageNumber = this.pageCount;
    },
    onchange(size) {
      this.sizeperpage = size;
      this.pageNumber = 1;
    },
    async loadSessionHistory() {
      if (!this.session.id) return;

      this.isLoading = true;
      try {
        await this.fetchallDevices(this.params);
      } catch (error) {
        console.error("Error loading session history:", error);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString();
    },
    getStatusText(session) {
      if (!session.startDate || !session.endDate) return "";

      const now = new Date();
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);

      if (now < startDate) return "Scheduled";
      if (now >= startDate && now <= endDate) return "Active";
      return "Completed";
    },
    getStatusClass(row) {
      if (!row.deviceStatus) return "status-unknown";
      return row.deviceStatus.toLowerCase() === "stable"
        ? "status-stable"
        : "status-critical";
    },

    getExcursionValue(obj, key) {
      if (!obj.lastDeviceAlertExcursion) return "";
      if (!obj.lastDeviceAlertExcursion[key]) return "";
      return obj.lastDeviceAlertExcursion[key];
    },

    getTemperatureClass(row) {
      if (!row.deviceSettings) return "";
      if (row.temperature < row.deviceSettings.minTemperature)
        return "text-primary";
      if (row.temperature > row.deviceSettings.maxTemperature)
        return "text-danger";
      return "";
    },

    getBatteryClass(level) {
      if (!level) return "";
      if (level >= 70) return "battery-high";
      if (level >= 30) return "battery-medium";
      return "battery-low";
    },

    fetchDeviceCB() {
      this.devicesclone = this.devices;
      this.sorteddevices = this.devices;
      this.isLoading = false;
    },

    exportSessionsHistoryCsv() {
      // Use selected rows if any, otherwise use all visible rows
      const dataToExport =
        this.selectedRows.length > 0
          ? this.paginatedData.filter((row) =>
              this.selectedRows.includes(row.id)
            )
          : this.paginatedData;

      if (!dataToExport.length) {
        // Show a message if no data to export
        this.$toast.warning(this.$t("no_data_to_export"));
        return;
      }

      // Define CSV headers
      const headers = [
        this.$t("status"),
        this.$t("last_seen"),
        this.$t("device_type"),
        this.$t("device_name"),
        "IMEI",
        // this.$t("excursion"),
        this.$t("temperature"),
        this.$t("humidity"),
        this.$t("battery"),
      ];

      // Prepare CSV data
      const csvData = dataToExport.map((row) => ({
        status: `"${row.deviceStatus || "N/A"}"`,
        lastSeen: `"${this.formatDate(row.updatedAt) || "N/A"}"`,
        deviceType: `"${(row.device?.type || "N/A").toLowerCase()}"`,
        deviceName: `"${row.device?.settings?.name || "N/A"}"`,
        imei: `"${row.device?.imei || "N/A"}"`,
        // excursion: `"${
        //   this.getExcursionValue(row, "heatingOrFreezingExcursionValue") ||
        //   "N/A"
        // }"`,
        temperature: `"${
          row.temperature ? row.temperature.toFixed(1) + "°C" : "N/A"
        }"`,
        humidity: `"${row.humidity ? row.humidity + "%" : "N/A"}"`,
        battery: `"${row.battery ? row.battery + "%" : "N/A"}"`,
      }));

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => Object.values(row).join(",")),
      ].join("\n");

      // Create and trigger download
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `session_history_${this.sessionName}_${timestamp}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    handleStatusChange() {
      this.pageNumber = 1;
      this.loadSessionHistory();
    },
    async doSearch() {
      const params = {
        ...this.params,
        ...(this.searchQuery && { keyword: this.searchQuery }),
        ...(this.statusFilter !== "all" && { status: this.statusFilter }),
      };
      this.isLoading = true;
      try {
        await this.fetchallDevices(params);
      } catch (error) {
        console.error("Error loading session history:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    // Get session ID from route params
    this.session.id = this.$route.params.id;

    // Get session data from Vuex store
    if (this.selectedSession && this.selectedSession.id === this.session.id) {
      // Merge the selected session data with our local session object
      Object.assign(this.session, this.selectedSession);

      // Update page title with session name
      document.title = `${this.sessionName} - Session History`;

      // Load session history data - only here, not in the watcher
      await this.loadSessionHistory();
    } else {
      // If no session in store, redirect back to sessions list
      this.$router.push({ name: "Sessions" });
    }
    this.$root.$on(
      "trigger-export-session-history-csv",
      this.exportSessionsHistoryCsv
    );
  },
  beforeDestroy() {
    this.$root.$off(
      "trigger-export-session-history-csv",
      this.exportSessionsHistoryCsv
    );
  },
  watch: {
    pageNumber() {
      this.loadSessionHistory();
    },
    sizeperpage() {
      this.pageNumber = 1;
      this.loadSessionHistory();
    },
    // Watch for route changes (not immediate, so it won't trigger on mount)
    "$route.params.id": {
      handler(newId) {
        if (newId && newId !== this.session.id) {
          this.session.id = newId;
          this.loadSessionHistory();
        }
      },
    },
    devices() {
      this.dataTable = this.devices;
    },
    searchQuery() {
      this.pageNumber = 1;
      this.debouncedSearch();
    },
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== "DeviceDetails") this.clearSelectedSession();
    next();
  },
};
</script>

<style scoped>
.data-table-bg {
  background-color: rgba(240, 244, 251, 0.35);
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-stable {
  background-color: #28a745;
}

.status-critical {
  background-color: #dc3545;
}

.status-unknown {
  background-color: #6c757d;
}

.excursion-badge {
  background-color: #ffc107;
  color: #212529;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 500;
}

.battery-indicator {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.9em;
}

.battery-high {
  background-color: #d4edda;
  color: #155724;
}

.battery-medium {
  background-color: #fff3cd;
  color: #856404;
}

.battery-low {
  background-color: #f8d7da;
  color: #721c24;
}

.text-primary {
  color: #007bff;
}

.text-danger {
  color: #dc3545;
}
</style>
